<template>
    <div>
        <div v-if="isLoading" class="text-center">
            <span class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
        </div>

        <div v-if="error" class="alert alert-danger mb-3" role="alert">
            {{ $t('checkout.confirmation_error') }}
            <button class="btn btn-sm btn-outline-light ms-2" @click="redirectToCart">
                {{ $t('checkout.return_to_cart') }}
            </button>
        </div>

        <div v-if="!isLoading && !error" class="row g-5 g-xl-3 justify-content-between">
            <div class="mb-5">
                <h2 class="mb-2">{{ $t('checkout.complete_thank_you') }}</h2>
                <h5 class="mb-0">{{ $t('checkout.complete_order_number') }} {{ order.orderNumber }}</h5>
                <p class="mt-2">{{ $t('checkout.confirmation_email_sent') }} <strong>{{ customer.email }}</strong></p>
                <div v-if="hasCallerAlertProduct" class="alert alert-danger">
                    <p class="mt-2" >
                        <b>{{ $t('checkout.caller_alert') }}</b>
                    </p>
                </div>
            </div>

            <div class="column col-12 col-xl-7">
                <div class="row g-4">
                    <div class="col-12 col-md-6">
                        <h5 class="mb-2">{{ $t('checkout.confirm_continue_your_info') }}</h5>

                        <p>
                            {{ billingAddress.firstName }} {{ billingAddress.lastName }}<br />
                            {{ billingAddress.street }}<br />
                            {{ billingAddress.zipcode }} {{ billingAddress.city }}<br />
                            {{ billingAddress.country?.name }}
                        </p>

                        <p>{{ customer.email }}</p>
                    </div>

                    <div class="col-12 col-md-6">
                        <template v-if="shippingMethod && shippingMethod !== 'shipping_dealer'">
                            <h5 class="mb-2">{{ $t('checkout.confirm_continue_delivery') }}</h5>

                            <p>
                                {{ shippingAddress.firstName }} {{ shippingAddress.lastName }}<br />
                                {{ shippingAddress.street }}<br />
                                {{ shippingAddress.zipcode }} {{ shippingAddress.city }}<br />
                                {{ shippingAddress.country?.name }}
                            </p>
                        </template>

                        <template v-if="shippingMethod && shippingMethod === 'shipping_dealer'">
                            <h5 v-if="shippingMethod === 'shipping_dealer'" class="mb-2"> {{
                                    $t('checkout.confirm_continue_pickup') }}</h5>
                            <p class="small mb-1">{{ $t('checkout.confirm_continue_free_pickup_text') }}</p>

                            <p class="h5 text-dark">{{ shippingAddress.company }}</p>
                            <p>{{ shippingAddress.street }},<br />
                                {{ shippingAddress.zipcode }} {{ shippingAddress.city }}<br />
                                {{ shippingAddress.country?.name }}</p>
                        </template>
                    </div>
                </div>

                <hr class="my-4" />

                <h5 class="mb-2">{{ $t('checkout.payment_method') }}</h5>

                <div class="payment-method-inputs">
                    <div class="form-check ps-0">
                        <label class="form-check-label mx-0">
                            <img v-if="paymentMethod.media?.url" class="thumb" :src="paymentMethod.media?.url" alt="Credit card - Thumb">
                            <div>
                                <span class="name">{{ paymentMethod?.translated?.name }}</span>
                            </div>
                        </label>
                    </div>
                </div>

                <hr class="my-4" />

                <h5 class="mb-2">{{ $t('checkout.complete_products_in_order') }}</h5>

                <div class="cart-summary">
                    <div class="line labels">
                        <div class="line-col product">
                            {{ $t('checkout.cart_label_product') }}
                        </div>
                        <div class="line-col count">
                            {{ $t('checkout.cart_label_count') }}
                        </div>
                        <div class="line-col item-price">
                            {{ $t('checkout.cart_label_item_price') }}
                        </div>
                        <div class="line-col line-price">
                            {{ $t('checkout.cart_label_line_price') }}
                        </div>
                    </div>

                    <div class="line item" v-for="lineItem in lineItems" :key="lineItem.id">
                        <div class="line-col product">
                            <div class="thumb">
                                <img v-if="lineItem.type == 'product'" :src="lineItem?.payload.coverImg ? lineItem?.payload.coverImg : '/images/product-placeholder.jpg'" />
                            </div>
                            <div v-if="isVoucher(lineItem)" class="meta">
                                <p class="title">{{ lineItem?.label }}</p>
                            </div>
                            <div v-else class="meta">
                                <a v-if="lineItem?.payload.productUrl" :href="lineItem?.payload.productUrl" class="title">
                                    {{ lineItem?.label }}
                                    <span v-if="lineItem?.payload?.variant">({{ getVariantLabel(lineItem?.payload?.variant) }})</span>
                                </a>
                                <p v-else class="title">
                                    {{ lineItem?.label }}
                                    <span v-if="lineItem?.payload?.variant">({{ getVariantLabel(lineItem?.payload?.variant) }})</span>
                                </p>

                                <p v-if="lineItem?.payload.description" class="description mb-0">{{ lineItem?.payload?.description }}</p>
                                <small v-if="lineItem?.payload.productVoucherCodeProduct" class="info">{{ $t("checkout.cart_label_voucher") }} {{ formatVoucher(lineItem?.payload.productVoucherCodeProduct, !isLifestyle) }}</small>
                                <small v-if="showStockStatus(lineItem)" class="warning">{{ $t(`product_detail.${lineItem.payload.stockStatus}_description`) }}</small>
                            </div>
                        </div>
                        <div class="line-col count static">
                            <span>{{ lineItem.quantity }}</span>
                        </div>
                        <div class="line-col item-price">
                            <span>&euro; {{ formatNumber(lineItem?.unitPrice) }}</span>
                        </div>
                        <div class="line-col line-price">
                            <span>&euro; {{ formatNumber(lineItem?.totalPrice) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="column col-12 col-xl-4">
                <div class="sticky-top">
                    <div class="card checkout-card">
                        <div class="card-body">
                            <h3 class="mb-3">{{ $t('checkout.payment_amount_payed') }}</h3>

                            <div class="label-value-row">
                                <span class="label">{{ $t('checkout.payment_total') }}</span>
                                <span class="value fw-bold">€ {{ positionPrice }}</span>
                            </div>

                            <div class="label-value-row">
                                <span class="label">{{ $t('checkout.payment_delivery_cost') }}</span>
                                <span v-if="shippingTotal <= 0" class="value fw-bold">-</span>
                                <span v-if="shippingTotal > 0" class="value fw-bold">€ {{ formatNumber(shippingTotal)
                                    }}</span>
                            </div>

                            <div v-if="hasDiscounts" class="label-value-row text-success">
                                <span class="label">{{ $t('checkout.payment_discounts') }}</span>
                                <span class="value fw-bold">-€ {{ formatNumber(discountTotal) }}</span>
                            </div>

                            <hr />

                            <div class="label-value-row">
                                <span class="label">{{ $t('checkout.payment_final_total') }}</span>
                                <span class="value fw-bold">€{{ formatNumber(totalPrice) }}</span>
                            </div>
                        </div>
                    </div>

<!--                    <div class="mt-3 d-flex justify-content-between">-->
<!--                        <a :href="homepageUrl" class="btn btn-outline-secondary">-->
<!--                            {{ $t('checkout.continue_shopping') }}-->
<!--                        </a>-->

<!--                        <a :href="accountOrdersUrl" class="btn btn-secondary">-->
<!--                            {{ $t('checkout.view_orders') }}-->
<!--                        </a>-->
<!--                    </div>-->
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import formatters from '../../mixins/formatters';
import lineitem from '../../mixins/line-item';

export default {
    mixins: [shopware, lineitem, formatters],
    props: {
        redirect: String,
        homepageUrl: {
            type: String,
            default: '/'
        },
        accountOrdersUrl: {
            type: String,
            default: '/account/orders'
        },
        isLifestyle: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasCallerAlertProduct() {
            return this.lineItems?.some(
                item => item?.payload?.productNumber == "1003207"
            )
        }
    },
    data() {
        return {
            isLoading: false,
            error: null,
            order: {},
            customer: {},
            actualCustomer: null, // Full customer data loaded separately
            billingAddress: {},
            shippingAddress: {},
            shippingMethod: null,
            positionPrice: null,
            shippingTotal: null,
            totalPrice: null,
            paymentMethod: {},
            lineItems: [],
            discountTotal: 0,
            hasDiscounts: false,
            retryCount: 0,
            maxRetries: 3
        };
    },
    created: function () {
        this.init();
    },
    methods: {
        async init() {
            this.isLoading = true;
            this.error = null;

            try {
                const orderId = this.getOrderIdFromUrl() || localStorage.getItem('lastOrderId');

                // If no order ID is found, redirect to cart
                if (!orderId) {
                    throw new Error("No order ID found");
                }

                await this.api.updateContext();
                await this.getOrder(orderId);

                // Track purchase event for analytics
                this.trackPurchaseEvent();

                // Clear guest data if this was a guest checkout (similar to Shopware's logoutGuestAfterCheckout)
                await this.clearGuestDataAfterOrder();

                // Clear storage once we've successfully loaded the confirmation
                this.clearOrderStorage();
            } catch (error) {
                console.error("Error initializing order confirmation:", error);
                this.error = error.message || this.$t('checkout.confirmation_error');

                // Retry logic for temporary errors
                if (this.retryCount < this.maxRetries && !error.message.includes("No order ID found")) {
                    this.retryCount++;
                    console.log(`Retrying order fetch (${this.retryCount}/${this.maxRetries})...`);
                    setTimeout(() => {
                        this.init();
                    }, 1000 * this.retryCount); // Exponential backoff
                    return;
                }
            } finally {
                this.isLoading = false;
            }
        },

        getOrderIdFromUrl() {
            // Extract order ID from URL if present
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('orderId');
        },

        redirectToCart() {
            window.location.href = this.redirect;
        },

        clearOrderStorage() {
            localStorage.removeItem('lastOrderId');
            localStorage.removeItem('transactionId');
        },

        async clearGuestDataAfterOrder() {
            try {
                // Use the customer data we loaded separately in getOrder()
                const isGuest = this.actualCustomer && this.actualCustomer.guest === true;

                if (isGuest) {
                    console.log('Guest checkout detected, clearing guest session data');

                    // Clear the guest context similar to how Shopware handles logoutGuestAfterCheckout
                    await this.api.client.invoke("logout post /account/logout", {});

                    // The logout call above should handle:
                    // 1. Deleting the cart
                    // 2. Deleting the context token from sales_channel_api_context
                    // 3. Generating a new context token
                    // This mimics Shopware's native logoutGuestAfterCheckout behavior

                    console.log('Guest checkout data cleared successfully');
                } else {
                    console.log('Registered customer order detected - no session clearing needed');
                }
            } catch (error) {
                // Guest data clearing failed, but don't break the user experience
                console.warn('Failed to clear guest data after checkout:', error);

                // Conservative fallback: only clear cookies if we detected a guest
                if (this.actualCustomer && this.actualCustomer.guest === true) {
                    try {
                        // Clear the context token cookie as a fallback using native cookie method
                        document.cookie = 'sw-context-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=lax';
                        console.log('Fallback: Guest context token cookie cleared');
                    } catch (cookieError) {
                        console.warn('Failed to clear context token cookie:', cookieError);
                    }
                }
            }
        },

        getGTMItems() {
            return this.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.unitPrice,
                quantity: item.quantity,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        },

        trackPurchaseEvent() {
            if (this.order && this.lineItems && this.lineItems.length > 0) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'purchase',
                    'transaction_id': localStorage.getItem('transactionId') || this.order.id,
                    'order_id': this.order.orderNumber,
                    'value': this.totalPrice,
                    'tax': this.order.price?.calculatedTaxes?.[0]?.tax || 0,
                    'shipping': this.shippingTotal || 0,
                    'currency': 'EUR',
                    'items': this.getGTMItems()
                });
            }
        },

        async getOrder(orderId) {
            try {
                const response = await this.api.client.invoke('readOrder post /order', {
                    limit: 1,
                    ids: [orderId],
                    associations: {
                        lineItems: {},
                        billingAddress: {
                            associations: {
                                country: {}
                            }
                        },
                        deliveries: {
                            associations: {
                                shippingMethod: {},
                                shippingOrderAddress: {
                                    associations: {
                                        country: {}
                                    }
                                }
                            }
                        },
                        transactions: {
                            associations: {
                                paymentMethod: {
                                    associations: {
                                        media: {}
                                    }
                                }
                            }
                        }
                    }
                });

                if (!response.orders || !response.orders.elements || response.orders.elements.length === 0) {
                    throw new Error("Order not found");
                }

                this.order = response.orders.elements[0];
                this.lineItems = this.order.lineItems || [];
                this.customer = this.order.orderCustomer || {};
                this.billingAddress = this.order.billingAddress || {};

                // Load customer data using customerNumber from the order
                if (this.order.orderCustomer?.customerNumber) {
                    try {
                        const customerResponse = await this.api.client.invoke("readCustomer post /account/customer", {
                            filter: [{
                                type: 'equals',
                                field: 'customerNumber',
                                value: this.order.orderCustomer.customerNumber
                            }]
                        });

                        this.actualCustomer = customerResponse;
                    } catch (customerError) {
                        console.warn('Could not load customer data by customerNumber:', customerError);
                        this.actualCustomer = null;
                    }
                } else {
                    console.warn('No customerNumber found in orderCustomer');
                    this.actualCustomer = null;
                }

                if (this.order.deliveries && this.order.deliveries.length > 0) {
                    this.shippingAddress = this.order.deliveries[0].shippingOrderAddress || {};
                    this.shippingMethod = this.order.deliveries[0].shippingMethod?.technicalName || null;
                    this.shippingTotal = this.order.deliveries[0].shippingCosts?.totalPrice || 0;
                }

                this.positionPrice = this.order.positionPrice || 0;
                this.totalPrice = this.order.price?.totalPrice || 0;

                if (this.order.transactions && this.order.transactions.length > 0) {
                    this.paymentMethod = this.order.transactions[0].paymentMethod || {};
                }

                // Calculate any discounts
                this.calculateDiscounts();
            } catch (error) {
                console.error("Error fetching order:", error);
                throw error;
            }
        },

        calculateDiscounts() {
            this.discountTotal = 0;
            this.hasDiscounts = false;

            // Check for promotion line items
            if (this.lineItems) {
                const promotionItems = this.lineItems.filter(item =>
                    item.type === 'promotion' || (item.good === false && item.totalPrice < 0)
                );

                if (promotionItems.length > 0) {
                    this.discountTotal = promotionItems.reduce(
                        (sum, item) => sum + Math.abs(item.totalPrice), 0
                    );
                    this.hasDiscounts = true;
                }
            }
        },

        getVariantLabel(variant) {
            // Handle localized Maat attribute stored as object in cart payload
            if (variant && typeof variant === 'object' && variant.code) {
                return variant.code;
            }
            return variant;
        }
    }
}
</script>
